import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Card, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';

import { ExtendedCustomerData } from '../../../backendsdk';
import {
    TrackedButton as Button,
    TrackedDialog as Dialog,
    TrackedIconButton as IconButton,
} from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useProfile from '../../../hooks/profile';
import { unformatPhoneNumber } from '../../../utils/Str';
import { PHONE_NUMBER_MASK_MAP } from '../UserSettings/UserSettings';
import { isValidPhoneNumber } from '../Users/validation';

interface AssignGuestTechnicianModalProps {
    isLoading: boolean;
    setIsLoading: CallableFunction;
    assignedMalfunctionIds: number[];
    onClose: CallableFunction;
    onAssign: CallableFunction;
    setAlert: CallableFunction;
}

const AssignGuestTechnicianModal: React.FC<AssignGuestTechnicianModalProps> = (props) => {
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const { profile } = useProfile();
    const { api } = useApi();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const country = profile.customer.settings.country;

    const isValid = !!firstName && !!lastName && isValidPhoneNumber(phoneNumber);

    const handleSend = () => {
        props.setIsLoading(true);
        api.apiV0MalfunctionGuestPost({
            guestTechnicianRequest: {
                first_name: firstName,
                last_name: lastName,
                phone_number: unformatPhoneNumber(phoneNumber),
            },
        })
            .then((res) => {
                queryClient.setQueryData<ExtendedCustomerData>(['customer'], (prev) => {
                    if (!!prev) {
                        return {
                            ...prev,
                            users: [...prev.users, res.data],
                        };
                    }
                    return prev;
                });
                props.onAssign(res.data.user_id);
                // setIsLoading(false) is called in onAssign
            })
            .catch(() => {
                props.setIsLoading(false);
                props.setAlert({
                    message: t('content.malfunctions.assign_to_guest.error'),
                    type: 'error',
                    duration: 6000,
                });
            });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const phoneInput: any = () => {
        const inputElement = (
            <TextField
                id="phone-number-field"
                size="small"
                label={t('content.malfunctions.assign_to_guest.phone_number')}
                inputProps={{ style: { direction: 'ltr' }, 'data-testid': 'phone-number-field' }}
                sx={{ mt: 1.5 }}
            />
        );
        return inputElement;
    };

    return (
        <Dialog
            id="assign-to-guest-dialog"
            open={true}
            maxWidth="xs"
            fullWidth
            onClose={() => {
                if (!props.isLoading) {
                    props.onClose();
                }
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    top: 0,
                    p: 1,
                    zIndex: 2,
                    minHeight: 0,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ position: 'absolute', display: 'flex', right: 0, top: 0, mt: 0.5, mr: 0.5 }}>
                    <IconButton
                        id="btn-close-modal"
                        size="small"
                        disabled={props.isLoading}
                        onClick={() => props.onClose()}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AssignmentIndIcon sx={{ mr: 0.5 }} />
                    <Typography>{t('content.malfunctions.assign_to_guest.header')}</Typography>
                </Box>
            </Box>
            <Box sx={{ width: '100%', p: 1 }}>
                <Card
                    sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        width: '100%',
                        minHeight: 0,
                        p: 1,
                        pt: 1.5,
                    }}
                >
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <TextField
                            fullWidth
                            size="small"
                            id="first-name-field"
                            label={t('content.malfunctions.assign_to_guest.first_name')}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            sx={{ mr: 1.5 }}
                            inputProps={{ 'data-testid': 'first-name-field' }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            id="last-name-field"
                            label={t('content.malfunctions.assign_to_guest.last_name')}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            inputProps={{ 'data-testid': 'last-name-field' }}
                        />
                    </Box>
                    <InputMask
                        mask={PHONE_NUMBER_MASK_MAP[country]}
                        value={phoneNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
                        maskChar=""
                    >
                        {phoneInput}
                    </InputMask>
                </Card>
                <Box sx={{ width: '100%', display: 'flex', mt: 1 }}>
                    <Button
                        fullWidth
                        id="btn-cancel"
                        data-testid="btn-cancel"
                        variant="contained"
                        color="primary"
                        disabled={props.isLoading}
                        onClick={() => props.onClose()}
                        sx={{ mr: 1 }}
                    >
                        {t('content.malfunctions.assign_to_guest.cancel')}
                    </Button>
                    <Button
                        fullWidth
                        id="btn-send"
                        data-testid="btn-send"
                        variant="contained"
                        color="secondary"
                        disabled={!isValid || props.isLoading}
                        onClick={() => handleSend()}
                    >
                        {t('content.malfunctions.assign_to_guest.send')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default AssignGuestTechnicianModal;
